import React, { useState } from "react";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { usePWA } from "../../../hooks/usePWA";
import {
  FaTabletAlt,
  FaQrcode,
  FaKey,
  FaShieldAlt,
  FaLock,
  FaCheckCircle,
  FaBuilding,
  FaArrowRight,
  FaUserTie,
  FaMobileAlt,
  FaDownload,
  FaExpand,
  FaCompress
} from "react-icons/fa";
import "./KioskAppleTheme.css";

const KioskDeviceAuthScreen = ({ onAuthorized, onBypass }) => {
  const { isStandalone, isFullscreen, promptInstall, toggleFullscreen } = usePWA();
  const [authMode, setAuthMode] = useState("pin"); // "pin" | "login"
  const [pinDigits, setPinDigits] = useState(["", "", "", "", "", ""]);
  const [deviceName, setDeviceName] = useState("Showroom iPad");
  const [loading, setLoading] = useState(false);

  // Salesperson direct login fallback state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleDigitChange = (index, value) => {
    const val = value.slice(-1).toUpperCase();
    const newDigits = [...pinDigits];
    newDigits[index] = val;
    setPinDigits(newDigits);

    // Auto-advance to next input
    if (val && index < 5) {
      const nextInput = document.getElementById(`kiosk-pin-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // Auto-submit when all 6 digits entered
    if (val && index === 5 && newDigits.every((d) => d !== "")) {
      submitPin(newDigits.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      const prevInput = document.getElementById(`kiosk-pin-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const submitPin = async (codeToSubmit) => {
    const code = codeToSubmit || pinDigits.join("");
    if (code.length !== 6) {
      toast.error("Please enter all 6 digits of the Pairing Code.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/session/pair`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pairingCode: code,
          deviceName: deviceName || "Showroom Tablet",
          userAgent: navigator.userAgent
        })
      });

      const data = await res.json();
      if (res.ok && data.success && data.data) {
        toast.success("✓ Kiosk Device Authorized Successfully!");
        localStorage.setItem("d2_kiosk_session_token", data.data.sessionToken);
        localStorage.setItem("d2_kiosk_salesperson_id", data.data.salespersonId);
        localStorage.setItem("d2_kiosk_salesperson_name", data.data.salespersonName);
        localStorage.setItem("d2_kiosk_device_name", data.data.deviceName);
        if (data.data.exitPin) localStorage.setItem("d2_kiosk_exit_pin", data.data.exitPin);

        if (onAuthorized) {
          onAuthorized(data.data);
        }
      } else {
        toast.error(data.message || "Invalid or expired pairing code.");
      }
    } catch (err) {
      console.error("Kiosk Pairing Error:", err);
      toast.error("Failed to connect to server. Please check internet connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleSalespersonLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password.");
      return;
    }

    setLoading(true);
    try {
      // Login via standard auth endpoint
      const loginRes = await fetch(`${getBackendBaseUrl()}/user/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });

      const loginData = await loginRes.json();
      if (loginRes.ok && (loginData.token || loginData.authToken || loginData.data?.token)) {
        const authToken = loginData.token || loginData.authToken || loginData.data?.token;

        // Generate kiosk session for this salesperson
        const sessionRes = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/session/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
          },
          body: JSON.stringify({
            deviceName: deviceName || "Showroom Tablet"
          })
        });

        const sessionData = await sessionRes.json();
        if (sessionRes.ok && sessionData.success && sessionData.data) {
          toast.success("✓ Tablet Authorized as Kiosk PWA!");
          localStorage.setItem("d2_kiosk_session_token", sessionData.data.sessionToken);
          localStorage.setItem("d2_kiosk_salesperson_id", sessionData.data.salespersonId);
          localStorage.setItem("d2_kiosk_salesperson_name", sessionData.data.salespersonName);
          localStorage.setItem("d2_kiosk_device_name", sessionData.data.deviceName);
          if (sessionData.data.exitPin) localStorage.setItem("d2_kiosk_exit_pin", sessionData.data.exitPin);

          if (onAuthorized) {
            onAuthorized(sessionData.data);
          }
          return;
        }
      }
      toast.error(loginData.message || "Invalid credentials.");
    } catch (err) {
      console.error("Kiosk Salesperson Login Error:", err);
      toast.error("Authentication failed. Please verify credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] w-full kiosk-apple-bg text-white flex flex-col justify-between items-center p-3 sm:p-6 relative overflow-y-auto font-sans select-none kiosk-safe-all">
      {/* Dynamic Aurora & Mesh Background Elements */}
      <div className="kiosk-ambient-mesh-layer" />
      <div className="kiosk-aurora-glow kiosk-aurora-1" />
      <div className="kiosk-aurora-glow kiosk-aurora-2" />

      {/* Top Header */}
      <div className="w-full max-w-4xl flex items-center justify-between z-10 animate-fadeInDown pt-1 pb-4">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-tr from-cyan-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/20 border border-white/20 flex-shrink-0">
            <FaTabletAlt size={16} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest kiosk-text-gradient-cyan">
                SaleSense Kiosk PWA
              </span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[9px] sm:text-[10px] text-emerald-400 font-bold">
                Device Lock Active
              </span>
            </div>
            <h1 className="text-sm sm:text-base font-bold text-slate-200 truncate">
              Showroom Tablet Setup &amp; Authorization
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isStandalone && (
            <button
              type="button"
              onClick={promptInstall}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-600 text-slate-950 text-xs font-black uppercase tracking-wider shadow-md hover:scale-105 active:scale-95 transition-transform cursor-pointer"
              title="Install Kiosk as Standalone App"
            >
              <FaDownload size={11} />
              <span>Install App</span>
            </button>
          )}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 text-xs transition-colors cursor-pointer"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen Mode"}
          >
            {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
          </button>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-400">
            <FaShieldAlt className="text-cyan-400" />
            <span>Customer Safe Sandbox</span>
          </div>
        </div>
      </div>

      {/* Main Glass Card */}
      <div className="w-full max-w-lg kiosk-apple-card border border-white/15 p-4 sm:p-8 rounded-2xl sm:rounded-3xl shadow-2xl relative z-10 animate-scaleUp my-auto">
        {/* Toggle Mode */}
        <div className="flex items-center justify-center p-1 bg-black/40 rounded-2xl border border-white/10 mb-6 sm:mb-8">
          <button
            type="button"
            onClick={() => setAuthMode("pin")}
            className={`flex-1 py-2 sm:py-2.5 rounded-xl text-[11px] sm:text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${
              authMode === "pin"
                ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-md font-extrabold"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <FaKey size={11} />
            <span>6-Digit Pairing PIN</span>
          </button>
          <button
            type="button"
            onClick={() => setAuthMode("login")}
            className={`flex-1 py-2 sm:py-2.5 rounded-xl text-[11px] sm:text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${
              authMode === "login"
                ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-md font-extrabold"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <FaUserTie size={11} />
            <span>Staff Login</span>
          </button>
        </div>

        {authMode === "pin" ? (
          <div className="space-y-4 sm:space-y-6 text-center">
            <div>
              <h2 className="text-xl sm:text-2xl font-black kiosk-text-gradient-hero">
                Pair This Tablet
              </h2>
              <p className="text-[11px] sm:text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                Open <span className="text-cyan-400 font-bold">Salesperson Web App</span> &gt; Click <span className="text-white font-bold">"Launch Kiosk"</span> to view your active 6-digit Device Code.
              </p>
            </div>

            {/* 6-Digit PIN Boxes (Responsive on 390px iPhone 14/15) */}
            <div className="flex justify-center gap-1.5 sm:gap-2.5 py-2 sm:py-3">
              {pinDigits.map((digit, idx) => (
                <input
                  key={idx}
                  id={`kiosk-pin-${idx}`}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  autoFocus={idx === 0}
                  className="w-10 h-13 sm:w-12 sm:h-16 text-center text-xl sm:text-2xl font-mono font-black bg-black/50 border-2 border-white/20 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/20 rounded-xl sm:rounded-2xl outline-none transition-all text-cyan-300"
                />
              ))}
            </div>

            {/* Optional Device Nickname */}
            <div className="text-left space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Device Nickname (Optional)
              </label>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g. Showroom iPad #1"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 outline-none focus:border-cyan-400 transition-colors"
              />
            </div>

            {/* Submit Button */}
            <button
              onClick={() => submitPin()}
              disabled={loading}
              className="w-full py-3.5 sm:py-4 rounded-2xl kiosk-apple-btn-primary text-white font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Authorize &amp; Launch Kiosk PWA</span>
                  <FaArrowRight size={13} />
                </>
              )}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSalespersonLogin} className="space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-black kiosk-text-gradient-hero">
                Sales Consultant Authorization
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                Log in once to link this tablet to your dealership salesperson profile.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Dealership Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="salesperson@dealership.com"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-slate-500 outline-none focus:border-cyan-400 transition-colors"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-slate-500 outline-none focus:border-cyan-400 transition-colors"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Device Name
              </label>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g. Front Desk Surface"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-xs text-white placeholder-slate-500 outline-none focus:border-cyan-400 transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 mt-2 rounded-2xl kiosk-apple-btn-primary text-slate-950 font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Authorize Showroom Tablet</span>
                  <FaCheckCircle size={14} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Quick Demo Bypass */}
        {onBypass && (
          <div className="mt-6 pt-4 border-t border-white/10 text-center">
            <button
              type="button"
              onClick={onBypass}
              className="text-xs text-slate-400 hover:text-cyan-300 underline font-semibold transition-colors"
            >
              Skip Setup (Launch Demo Kiosk Mode) &rarr;
            </button>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="w-full max-w-lg flex items-center justify-between text-[11px] text-slate-400 z-10 animate-fadeInUp">
        <span className="flex items-center gap-1.5">
          <FaLock className="text-emerald-400" /> End-to-End Deal Encryption
        </span>
        <span>SaleSense Dealership 2.0 PWA</span>
      </div>
    </div>
  );
};

export default KioskDeviceAuthScreen;
